import React, { ReactElement, useEffect, useState } from 'react';
import './ResultsIndicator.less';
import MaterialTable, { Column, MTableBodyRow } from 'material-table';
import {
  Accordion, Button, Col, Row,
} from 'react-bootstrap';
import Select from 'react-select';
import { monthsArray } from '../../stores/StratProject/StrategyStore';
import { localization, modifyDefaultOptions, sortByAlphaNum } from '../../helpers/table';
import ResultsTable, { Milestone } from './ResultsTable';
import milestoneFactIcon from '../../../img/indicators/milestone_fact.svg';
import milestoneFactNotAchievedIcon from '../../../img/indicators/milestone_fact_not_achieved.svg';
import milestonePlanIcon from '../../../img/indicators/milestone_plan.svg';
import milestonePlanNotAchievedIcon from '../../../img/indicators/milestone_plan_not_achieved.svg';
import resultsFactIcon from '../../../img/indicators/results_fact.svg';
import resultsFactNotAchievedIcon from '../../../img/indicators/results_fact_not_achieved.svg';
import resultsPlanIcon from '../../../img/indicators/results_plan.svg';
import resultsPlanNotAchievedIcon from '../../../img/indicators/results_plan_not_achieved.svg';
import useAPI from '../../hooks/useAPI';
import { years } from '../../helpers';

export default function ResultsIndicator() {
  const api = useAPI();

  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [results, setResults] = useState<{[key: number]: Milestone[]}>({});
  const [filters, setFilters] = useState<IndicatorsFilters>({
    year: (new Date()).getFullYear(),
  });
  const [expanded, setExpanded] = useState<boolean>(true);

  function changeFilters(name: keyof IndicatorsFilters, value?: number | number[]) {
    setFilters((prevState) => ({ ...prevState, [name]: value }));
  }

  function getFilterRow() {
    return Object
      .entries(filters)
      .filter(([key, value]) => value !== undefined)
      .reduce((acc, [key, value], currentIndex, filtersArray) => `${acc}${key}=${Array.isArray(value) ? value.join(',') : value}${currentIndex === filtersArray.length - 1 ? '' : '&'}`, '?');
  }

  async function uploadResults() {
    const filterRow = getFilterRow();
    await api.download(
      `/strategies/results/export${filterRow.length === 1 ? '' : filterRow}`,
      'Отчет по Выдающимся результатам СП и вехам их достижений.xlsx',
    );
  }

  useEffect(() => {
    const getResults = async () => {
      const filterRow = getFilterRow();
      const respResults: Milestone[] = await api.get(`/strategies/results${filterRow.length === 1 ? '' : filterRow}`);
      const resultsByStrategy: {[key: string]: Milestone[]} = {};
      respResults.forEach((result) => {
        if (resultsByStrategy[result.strategyId]) {
          resultsByStrategy[result.strategyId].push(result);
          return;
        }
        resultsByStrategy[result.strategyId] = [result];
      });
      setResults(resultsByStrategy);
      setExpanded(true);
    };
    getResults();
  }, [filters]);

  useEffect(() => {
    const getStrategies = async () => {
      const type = filters.year && filters.year <= 2024 ? 'strategy' : JSON.stringify(['tech', 'goal']);
      const respStrategies: { id: number; num: string; name: string; isHidden: boolean }[] = await api.get(`/mng/strategies?type=${type}&year=${filters.year}`);
      setStrategies(respStrategies
        .filter((strategy) => !strategy.isHidden)
        .sort((a, b) => sortByAlphaNum(a.num, b.num))
        .map((strategy) => ({
          id: strategy.id,
          name: `${strategy.num} ${strategy.name}`,
        })));
    };
    getStrategies();
  }, [filters.year]);

  return (
    <div className="results">
      <h2 className="results-title">Выдающиеся результаты стратегических проектов/ целей и вехи их достижения</h2>
      <Row>
        <Col xxl={7} xs={10}>
          <Row className="results-filterRow">
            <Col xs={2} className="results-filterCol d-flex flex-column justify-content-start gap-2">
              <h2 className="results-label">Год</h2>
              <Select
                placeholder=""
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 100 }),
                }}
                options={years.map((year) => ({
                  label: year,
                  value: year,
                }))}
                onChange={(opt) => changeFilters('year', opt?.value)}
                value={{
                  label: filters.year,
                  value: filters.year,
                }}
              />
            </Col>
            <Col xs={3} className="results-filterCol d-flex flex-column justify-content-start gap-2">
              <h2 className="results-label">Месяц (план)</h2>
              <Select
                placeholder=""
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 100 }),
                  multiValue: (base) => ({ ...base, maxWidth: '45%' }),
                }}
                options={monthsArray.map((month, index) => ({
                  label: month,
                  value: index,
                }))}
                onChange={
                  (options) => changeFilters(
                    'month',
                    options.length !== 0 ? options.map((opt) => opt.value) : undefined,
                  )
                }
                isClearable
                isMulti
              />
            </Col>
            <Col xs={4} className="results-filterCol d-flex flex-column justify-content-start gap-2">
              <h2 className="results-label">Стратегический проект/ цель</h2>
              <Select
                key={`strategy-select-${filters.year}`}
                placeholder=""
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 100 }),
                }}
                options={strategies.map((strategy) => ({
                  label: strategy.name,
                  value: strategy.id,
                }))}
                onChange={(options) => changeFilters(
                  'strategyId',
                  options.length !== 0 ? options.map((opt) => opt.value) : undefined,
                )}
                isClearable
                isMulti
              />
            </Col>
            <Col xs={3} className="results-filterCol d-flex flex-column justify-content-start" style={{ marginTop: 28 }}>
              <Button variant="outline-primary" onClick={uploadResults}>Выгрузить в Excel</Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="results-legend mb-3">
        <div className="results-legendGrid">
          {milestoneIndicatorsLegend.map((legendItem) => (
            <div style={{ gridArea: legendItem.areaName, fontSize: 14 }}>
              <img src={legendItem.icon} />
              {' '}
              {legendItem.label}
            </div>
          ))}
        </div>
        <Row className="mt-3 flex-row justify-content-center">
          <Col xs={9}>
            <p className="results-hint">
              Веха - промежуточный этап достижения выдающегося результата стратегического проекта
            </p>
          </Col>
          <Col xs={3}>
            <Accordion
              flush
              onClick={() => setExpanded((prevState) => !prevState)}
              className="results-accordion"
            >
              <Accordion.Item eventKey="true">
                <Accordion.Header className="results-accordionHeader results-overrideHeader results-accordionHeaderOverride">
                  {expanded ? 'Свернуть' : 'Развернуть'}
                  {' '}
                  все вехи
                </Accordion.Header>
              </Accordion.Item>
            </Accordion>
          </Col>

        </Row>
      </Row>
      <div className="resultIndicators">
        <MaterialTable
          columns={[
            {
              title: 'Плановый результат / вехи',
              field: 'name',
              cellStyle: {
                minWidth: 560,
                borderBottom: '1px solid #BFBFD0',
                fontWeight: 600,
                padding: 12,
              },
            },
            {
              title: 'Срок (план)',
              field: 'planDate',
              cellStyle: {
                minWidth: 150,
                borderBottom: '1px solid #BFBFD0',
                padding: 12,
              },
            },
            ...['янв.', 'февр.', 'март', 'апр.', 'май', 'июнь', 'июль', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.']
              .map((month, index) => ({
                title: month,
                field: `month${index}`,
                cellStyle: {
                  minWidth: 85,
                  borderBottom: '1px solid #BFBFD0',
                  borderRight: index === monthsArray.length - 1 ? '1px solid #BFBFD0' : 'none',
                  padding: 12,
                },
                headerStyle: {
                  textAlign: 'center',
                },
              } as Column<Strategy>)),
          ]}
          data={strategies.filter((strategy) => results[strategy.id])}
          components={{
            Row: (props) => (
              <>
                <MTableBodyRow {...props} />
                <tr>
                  <td colSpan={14} style={{ border: 'none' }}>
                    <ResultsTable
                      results={
                        (results[props.data.id] || [])
                          .map((result) => ({ ...result, tableData: { isTreeExpanded: expanded } }))
                      }
                      filters={filters}
                    />
                  </td>
                </tr>
              </>
            ),
          }}
          options={modifyDefaultOptions({
            filtering: false,
            sorting: false,
            toolbar: false,
            headerStyle: {
              fontWeight: 600,
              borderTop: '1px solid black',
              borderBottom: '1px solid black',
              height: 55,
            },
            rowStyle: {
              backgroundColor: '#D9DDF3',
            },
            maxBodyHeight: 'calc(100vh - 310px)',
          })}
          localization={localization}
        />
      </div>
    </div>
  );
}

export enum MilestoneIndicatorTypes {
    planResult = 'planResult',
    planMilestone = 'planMilestone',
    planResultNotAchieved = 'planResultNotAchieved',
    planMilestoneNotAchieved = 'planMilestoneNotAchieved',
    factResult = 'factResult',
    factMilestone = 'factMilestone',
    factResultNotAchieved = 'factResultNotAchieved',
    factMilestoneNotAchieved = 'factMilestoneNotAchieved'
}

export const milestoneIndicatorsLegend: {label: string, icon: string, areaName: MilestoneIndicatorTypes}[] = [
  {
    label: 'План результата',
    icon: resultsPlanIcon,
    areaName: MilestoneIndicatorTypes.planResult,
  },
  {
    label: 'План вехи',
    icon: milestonePlanIcon,
    areaName: MilestoneIndicatorTypes.planMilestone,
  },
  {
    label: 'План результата не достигнут',
    icon: resultsPlanNotAchievedIcon,
    areaName: MilestoneIndicatorTypes.planResultNotAchieved,
  },
  {
    label: 'План вехи не достигнут',
    icon: milestonePlanNotAchievedIcon,
    areaName: MilestoneIndicatorTypes.planMilestoneNotAchieved,
  },
  {
    label: 'Факт результата',
    icon: resultsFactIcon,
    areaName: MilestoneIndicatorTypes.factResult,
  },
  {
    label: 'Факт вехи',
    icon: milestoneFactIcon,
    areaName: MilestoneIndicatorTypes.factMilestone,
  },
  {
    label: 'Факт результата достигнут не в срок',
    icon: resultsFactNotAchievedIcon,
    areaName: MilestoneIndicatorTypes.factResultNotAchieved,
  },
  {
    label: 'Факт вехи достигнут не в срок',
    icon: milestoneFactNotAchievedIcon,
    areaName: MilestoneIndicatorTypes.factMilestoneNotAchieved,
  },
];

type Strategy = {
    id: number,
    name: string
}

export type IndicatorsFilters = {
    month?: number[],
    year?: number,
    strategyId?: number[]
}
